import { QueryClient } from '@tanstack/react-query';
import { EntityMetadataModel, ExpressionMetadataModel, ParameterMetadata, PrimitiveType } from '../../../api/api';
import { sortByOrder } from '../../../common/sortByOrder';
import { FlowQueries } from '../Hooks/useFlowQueries';
import { ConstExpressionModel } from '../Models/ConstExpressionModel';
import { ExpressionModel } from '../Models/ExpressionModel';
import { ExpressionTypes } from '../Models/ExpressionTypes';
import { ObjectExpressionModel } from '../Models/ObjectExpressionModel';
import { ObjectPropertyType } from '../Models/ObjectPropertyType';
import { SchemaValueModel } from '../Models/SchemaValueModel';
import { VarExpressionModel } from '../Models/VarExpressionModel';
import { IVariableScope } from '../Models/VariableScopes/IVariableScope';
import { VariableScope } from '../Models/VariableScopes/VariableScope';
import { convertJsonSchemaPropertyToEntityProperty, getJsonSchemaFromProperty } from './EntityMetadataModelConverter';
import { VariableScopeCalculatorServiceBase } from './VariableScopeCalculatorServiceBase';
import { isScopeGuard, narrowVariableScope } from './SchemaScopeNarrowing';

export type ExpectedObjectTypeResolver = (
	parameterMetadata: ParameterMetadata,
	referencedParam: ParameterMetadata,
	model: Record<string, any>,
	parsed: {
		paramName: string;
		useRequestSchema?: boolean;
		useResponseSchema?: boolean;
		useInputSchema?: boolean;
		useOutputSchema?: boolean;
	},
) => Promise<EntityMetadataModel | undefined> | EntityMetadataModel | undefined;

export default class ExpressionVariableScopeCalculatorService extends VariableScopeCalculatorServiceBase {
	private expressionByName: Map<string, ExpressionMetadataModel>;
	private expectedObjectTypeResolver: ExpectedObjectTypeResolver | undefined;

	constructor(
		expressionMetadataList: ExpressionMetadataModel[],
		flowQueries: FlowQueries,
		queryClient: QueryClient,
		expectedObjectTypeResolver?: ExpectedObjectTypeResolver,
		schemaValues: SchemaValueModel[] = [],
	) {
		super(flowQueries, queryClient, schemaValues);
		this.expressionByName = new Map(expressionMetadataList.map((item) => [item.name, item]));
		this.expectedObjectTypeResolver = expectedObjectTypeResolver;
	}

	withSchemaValues(schemaValues: SchemaValueModel[]): ExpressionVariableScopeCalculatorService {
		return new ExpressionVariableScopeCalculatorService(
			Array.from(this.expressionByName.values()), this.flowQueries, this.queryClient,
			this.expectedObjectTypeResolver, schemaValues,
		);
	}

	protected override getExpressionMetadata(expressionType: string): ExpressionMetadataModel | undefined {
		return this.expressionByName.get(expressionType);
	}

	public override async resolveExpectedObjectType(
		parameterMetadata: ParameterMetadata,
		parameters: ParameterMetadata[],
		model: Record<string, any>,
	): Promise<EntityMetadataModel | undefined> {
		if (parameterMetadata.expectedType?.typeRef && this.expectedObjectTypeResolver) {
			const parsed = this.parseTypeRefSuffix(parameterMetadata.expectedType.typeRef);
			const referencedParam = parameters.find((p) => p.name === parsed.paramName);

			if (referencedParam) {
				const result = await Promise.resolve(this.expectedObjectTypeResolver(parameterMetadata, referencedParam, model, parsed));
				if (result) {
					return result;
				}
			}
		}
		return super.resolveExpectedObjectType(parameterMetadata, parameters, model);
	}

	/**
	 * Analyzes an expression to determine its result type.
	 * This is useful for understanding what type a collection expression produces
	 * to properly unwrap collection types when mapping over them.
	 *
	 * @param expression The expression to analyze
	 * @param variableScope The variable scope for resolving variable references
	 * @returns The ObjectPropertyType representing the expression's result type, or undefined if cannot be determined
	 */
	public analyzeExpressionType(expression: ExpressionModel, variableScope: IVariableScope): ObjectPropertyType | undefined {
		return this.analyzeExpressionRecursively(expression, variableScope);
	}

	/**
	 * Unwraps a collection type by one level.
	 * Collection<Collection<T>> -> Collection<T>
	 * Collection<T> -> T
	 *
	 * @param variableType The type to unwrap
	 * @returns The unwrapped type, or undefined if input is undefined
	 */
	public unwrapCollectionType(variableType: ObjectPropertyType | undefined): ObjectPropertyType | undefined {
		return this.applyCollectionWrapping(variableType, false, true);
	}

	/**
	 * Calculates the variable scope that would be available when evaluating a given expression's Target.
	 * This is useful for determining what variables are accessible when selecting paths in VarPathField.
	 *
	 * @param targetExpression The target expression to evaluate
	 * @param baseVariableScope The base variable scope to start from
	 * @returns The calculated variable scope, or the base scope if target cannot be resolved
	 */
	calculateVariableScopeFromTarget(targetExpression: ExpressionModel | undefined, baseVariableScope: IVariableScope | undefined): IVariableScope | undefined {
		if (!targetExpression || !baseVariableScope) {
			// scope is unknown
			return undefined;
		}

		// Handle Var expressions
		if (targetExpression.Type === ExpressionTypes.Var) {
			const varExpression = targetExpression as VarExpressionModel;

			let targetScope: IVariableScope | undefined = baseVariableScope;
			// If this var expression has a Target, we need to resolve the Target first
			if (varExpression.Target) {
				// Recursively calculate the scope from the Target expression
				targetScope = this.calculateVariableScopeFromTarget(varExpression.Target, baseVariableScope);
			}

			if (targetScope && varExpression.Value && varExpression.Value.length > 0) {
				// Resolve the Value path against the Target's scope.
				const resultType = this.getPropertyTypeFromVarExpression(varExpression, targetScope);
				if (resultType) {
					return this.getVariableScopeFromPropertyType(resultType);
				}
				// Path is non-empty but couldn't be resolved — return undefined rather than
				// silently returning targetScope, which would give misleading autocomplete results.
				return undefined;
			}

			// No Value path — the Var expression resolves to the target scope itself.
			return targetScope;
		}

		// Handle other expression types using metadata
		const expressionMetadata = this.expressionByName.get(targetExpression.Type);
		if (expressionMetadata) {
			const resultType = this.getResultTypeFromExpressionMetadata(targetExpression, expressionMetadata, baseVariableScope);
			return this.getVariableScopeFromPropertyType(resultType);
		}

		// scope is unknown
		return undefined;
	}

	/**
	 * Recursively analyzes an expression to determine its result type by examining metadata
	 * and following resultTypeRef chains
	 */
	private getResultTypeFromExpressionMetadata(
		expression: ExpressionModel,
		metadata: ExpressionMetadataModel,
		baseVariableScope: IVariableScope,
	): ObjectPropertyType | undefined {
		return this.analyzeExpressionResultType(
			expression, metadata, baseVariableScope, 'result', undefined,
			(nested, name, scope) => {
				const resolved = this.analyzeExpressionRecursively(nested, scope);
				return resolved ? { ...resolved, name } : undefined;
			},
			(name, type) => this.getVariableTypeAsPrimitive(name, type),
		);
	}

	/**
	 * Recursively analyzes any expression to determine its result type
	 */
	private analyzeExpressionRecursively(expression: ExpressionModel, baseVariableScope: IVariableScope): ObjectPropertyType | undefined {
		if (expression.Type === 'When') {
			const thenType = expression.Then ? this.analyzeExpressionRecursively(expression.Then, narrowVariableScope(baseVariableScope, expression.Condition, true)) : undefined;
			const elseType = expression.Else ? this.analyzeExpressionRecursively(expression.Else, narrowVariableScope(baseVariableScope, expression.Condition, false)) : undefined;
			if (thenType && (!expression.Else || elseType)) return convertJsonSchemaPropertyToEntityProperty('result', {
				anyOf: [getJsonSchemaFromProperty(thenType), elseType ? getJsonSchemaFromProperty(elseType) : { type: 'null' }],
			}, true);
			return undefined;
		}
		switch (expression.Type) {
			case ExpressionTypes.Var:
				return this.getPropertyTypeFromVarExpression(expression as VarExpressionModel, baseVariableScope);

			case ExpressionTypes.Const:
				return this.analyzeConstExpression(expression as ConstExpressionModel);

			case ExpressionTypes.Object:
				return this.analyzeObjectExpression(expression as ObjectExpressionModel, baseVariableScope);
		}

		// Fallback to basic expression type analysis
		const expressionMetadata = this.expressionByName.get(expression.Type);
		if (expressionMetadata) {
			return this.getResultTypeFromExpressionMetadata(expression, expressionMetadata, baseVariableScope);
		}

		// Unknown at result at this point
		return undefined;
	}

	/**
	 * Analyzes a constant expression to determine its result type
	 */
	private analyzeConstExpression(expression: ConstExpressionModel): ObjectPropertyType | undefined {
		return this.resolveConstExpressionType(expression, 'const');
	}

	/**
	 * Analyzes an object expression to determine its result type
	 */
	private analyzeObjectExpression(_expression: ObjectExpressionModel, _baseVariableScope: IVariableScope): ObjectPropertyType | undefined {
		// Object expressions always result in objects
		// TODO: We could potentially create a dynamic schema based on the properties
		// but for now, we'll return a generic object type
		return {
			name: 'object',
			type: PrimitiveType.Object,
			isComplex: true,
			isMultiple: false,
			isRequired: false,
		};
	}

	/**
	 * Resolves the expected primitive type(s) for a parameter, taking into account expectedTypeRef.
	 * If expectedTypeRef points to a VarExpression parameter, navigates the variable scope to determine the actual type.
	 *
	 * @param parameterMetadata The parameter metadata containing expectedType and expectedTypeRef
	 * @param expression The expression containing the parameter values
	 * @param variableScope The variable scope to use for resolving VarExpression types
	 * @param fallbackTypes The types to return if resolution fails (defaults to PRIMITIVE_TYPES_WITHOUT_REF)
	 * @returns Array of primitive types that match the parameter's expected type
	 */
	resolveExpectedPrimitiveTypes(
		parameterMetadata: ParameterMetadata,
		expression: ExpressionModel | undefined,
		variableScope: IVariableScope | undefined,
		fallbackTypes: PrimitiveType[],
	): PrimitiveType[] {
		// If expectedTypeRef is specified, resolve the type from the referenced parameter
		if (parameterMetadata.expectedType?.typeRef && expression) {
			const referencedParamName = parameterMetadata.expectedType.typeRef;

			// Get the referenced parameter value from the expression
			const referencedParamValue = expression[referencedParamName];
			if (referencedParamValue && typeof referencedParamValue === 'object' && referencedParamValue.Type === ExpressionTypes.Var && variableScope) {
				const varExp = referencedParamValue as VarExpressionModel;
				// Use getPropertyTypeFromVarExpression so that Var-with-Target expressions are
				// resolved against the Target's scope rather than the flat outer scope.
				const resolved = this.getPropertyTypeFromVarExpression(varExp, variableScope);
				if (resolved?.type) {
					return [resolved.type];
				}
			}
		}

		// expectedType is specified
		if (parameterMetadata.expectedType?.type) {
			return [parameterMetadata.expectedType.type];
		}

		// Fallback to provided types
		return fallbackTypes;
	}

	/**
	 * Calculates variable scopes for all parameters of an expression in a single O(N) forward pass.
	 * More efficient than calling calculateVariableScopeForParameter N times, which re-walks from
	 * the start for each parameter (O(N²) total processVariableSets calls).
	 *
	 * @param expression The expression being edited
	 * @param expressionMetadata The expression metadata
	 * @param baseVariableScope The starting variable scope
	 * @returns Map from parameter name to its calculated variable scope
	 */
	async calculateAllParameterScopes(
		expression: ExpressionModel,
		expressionMetadata: ExpressionMetadataModel,
		baseVariableScope: IVariableScope,
	): Promise<Map<string, IVariableScope>> {
		const result = new Map<string, IVariableScope>();
		const sortedParameters = [...expressionMetadata.parameters].sort(sortByOrder);

		for (const param of sortedParameters) {
			// Each parameter gets a fresh scope derived from the base — parameter-level
			// variableSets are visible ONLY within that parameter and do not accumulate
			// into sibling parameters' scopes.
			const paramScope = new VariableScope();
			paramScope.addPrevious(baseVariableScope as VariableScope);

			const expectedType = param.expectedType;
			if (expectedType?.variableSets?.length) {
				await this.processVariableSets(
					expectedType.variableSets,
					expressionMetadata.parameters,
					expression,
					paramScope,
					paramScope,
					`expression '${expression.Type}' parameter '${param.name}'`,
					(expr, name, scope) => {
						let resultType = this.analyzeExpressionRecursively(expr, scope);
						if (resultType) resultType = { ...resultType, name };
						return resultType;
					},
				);
			}

			result.set(param.name, expression.Type === 'When' && ['Then', 'Else'].includes(param.name)
				? narrowVariableScope(paramScope, expression.Condition, param.name === 'Then') : paramScope);
		}

		return result;
	}

	/**
	 * Calculates the variable scope that should be available when editing a specific parameter of an expression.
	 * This includes variables from the base scope plus any variables added by previous parameters' variableSets.
	 *
	 * @param expression The expression being edited
	 * @param parameterMetadata The parameter we're calculating scope for
	 * @param expressionMetadata The expression metadata
	 * @param baseVariableScope The starting variable scope
	 * @returns The calculated variable scope for this parameter
	 */
	async calculateVariableScopeForParameter(
		expression: ExpressionModel,
		parameterMetadata: ParameterMetadata,
		expressionMetadata: ExpressionMetadataModel,
		baseVariableScope: IVariableScope | undefined,
	): Promise<IVariableScope | undefined> {
		if (!baseVariableScope) {
			return undefined;
		}

		// Create a scope that inherits from the base scope and then applies ONLY the target
		// parameter's own variableSets. Parameter-level variableSets are scoped exclusively
		// to the parameter they are declared on — they do not accumulate into siblings.
		const parameterScope = new VariableScope();
		parameterScope.addPrevious(baseVariableScope as VariableScope);

		const expectedType = parameterMetadata.expectedType;
		if (expectedType?.variableSets?.length) {
			await this.processVariableSets(
				expectedType.variableSets,
				expressionMetadata.parameters,
				expression,
				parameterScope,
				parameterScope,
				`expression '${expression.Type}' parameter '${parameterMetadata.name}'`,
				(expr, name, scope) => {
					let resultType = this.analyzeExpressionRecursively(expr, scope);
					if (resultType) resultType = { ...resultType, name };
					return resultType;
				},
			);
		}

		return expression.Type === 'When' && ['Then', 'Else'].includes(parameterMetadata.name)
			? narrowVariableScope(parameterScope, expression.Condition, parameterMetadata.name === 'Then') : parameterScope;
	}

	/**
	 * Determines the property type that a var expression would resolve to.
	 * When the expression has a Target, the Value path is resolved against the Target's scope
	 * rather than the outer scope — otherwise properties like referenceEnum are lost because
	 * they only exist as members of the Target object, not as top-level variables.
	 */
	private getPropertyTypeFromVarExpression(varExpression: VarExpressionModel, variableScope: IVariableScope): ObjectPropertyType | undefined {
		const lastName = varExpression.Value?.[varExpression.Value.length - 1] ?? '';

		if (varExpression.Target) {
			const targetScope = this.calculateVariableScopeFromTarget(varExpression.Target, variableScope);
			if (!targetScope) return undefined;
			return this.resolveVarExpressionType(varExpression, targetScope, lastName);
		}

		return this.resolveVarExpressionType(varExpression, variableScope, lastName);
	}

	/**
	 * Computes the variables that a single sub-expression contributes to the outer scope.
	 * This inspects the expression metadata and processes each parameter's variableSets.
	 * Used to accumulate scope across sequential collection items (And, Or, etc.).
	 *
	 * @param subExpression The sub-expression to analyse
	 * @param contextScope The variable scope in effect when the sub-expression runs
	 * @returns A VariableScope containing only the variables introduced by this expression
	 */
	private async calculateVariablesContributedByExpression(subExpression: ExpressionModel, contextScope: IVariableScope): Promise<VariableScope> {
		const contributed = new VariableScope();
		await this.processExpressionVariableSetsInto(subExpression, contextScope, contributed, (expr, name, scope) => {
			let type = this.analyzeExpressionRecursively(expr, scope);
			if (type) type = { ...type, name };
			return type;
		});
		return contributed;
	}

	/**
	 * Builds per-item variable scopes for a collection expression parameter.
	 *
	 * For expressions like And / Or / AndParallel whose Value parameter is a collection
	 * of sub-expressions, each sub-expression can introduce new variables (e.g. via
	 * ResponseVariable). This method ensures that item[i] sees the variables contributed
	 * by items[0..i-1], matching the sequential execution model.
	 *
	 * @param expression The parent expression (And, Or, etc.)
	 * @param param The collection parameter metadata
	 * @param expressionMetadata The parent expression's metadata
	 * @param baseVariableScope The outer variable scope
	 * @param precomputedParamScope The already-computed scope for this param (avoids a redundant
	 *   calculateVariableScopeForParameter call when the caller has already computed all scopes).
	 * @returns One scope per item in the collection
	 */
	async calculateVariableScopesForCollectionParam(
		expression: ExpressionModel,
		param: ParameterMetadata,
		expressionMetadata: ExpressionMetadataModel,
		baseVariableScope: IVariableScope | undefined,
		precomputedParamScope?: IVariableScope,
	): Promise<(IVariableScope | undefined)[]> {
		if (!baseVariableScope) return [];

		const items = (expression[param.name] ?? []) as (ExpressionModel | undefined)[];
		if (items.length === 0) return [];

		const paramBaseScope = precomputedParamScope ?? (await this.calculateVariableScopeForParameter(expression, param, expressionMetadata, baseVariableScope));

		let currentScope: IVariableScope = paramBaseScope ?? baseVariableScope;
		const scopes: (IVariableScope | undefined)[] = [];
		const sequentialGuards = ['And', 'Or'].includes(expression.Type) && param.name === 'Value';
		for (const subExpr of items) {
			scopes.push(currentScope);
			if (!subExpr?.Type) { continue; }
			try {
				const contributed = await this.calculateVariablesContributedByExpression(subExpr, currentScope);
				if (contributed.getVariables().size) {
					const next = new VariableScope(new Map(currentScope.getVariables()));
					next.setVariables(contributed.getVariables());
					currentScope = next;
				}
				if (sequentialGuards && isScopeGuard(subExpr)) {
					currentScope = narrowVariableScope(currentScope, subExpr, expression.Type === 'And');
				}
			} catch {
				// Preserve the last valid scope if this expression cannot be resolved.
			}
		}
		return scopes;
	}
}
